import { patientLinkQrCode, qrSvgPath } from "../lib/qr/qrSvgPath";
import { shortPatientLink } from "../pages/schedule/visitIntake";
import "./walk-in-intake.css";

function QrImage({ url, label }: { readonly url: string; readonly label: string }) {
  const { viewBox, path, extent } = qrSvgPath(patientLinkQrCode(url));
  return (
    <svg
      className="walk-in-code-qr"
      role="img"
      aria-label={label}
      viewBox={viewBox}
      shapeRendering="crispEdges"
    >
      <rect className="walk-in-code-qr-plate" width={extent} height={extent} />
      <path className="walk-in-code-qr-modules" d={path} />
    </svg>
  );
}

/**
 * A patient link as the desk shows it (PA-5's phone code, reused by PA-10's "Ask for patient
 * details" and PA-12's booking-link code on Admin -> Scheduling): the clinic's name, the QR code,
 * the link as text and, when known, when it stops working. Never the patient's name, so the slip
 * can be printed and handed over (walk-in-intake.css prints only this slip). Its parent is
 * `.walk-in-code`, a direct child of a `ScheduleModal`, and keeps the URL only while the code is
 * on screen. The wording defaults to a questionnaire link.
 */
export function PatientLinkSlip({
  clinicName,
  url,
  expiry,
  scanText = "Scan with your phone camera to answer your visit questions.",
  qrLabel = "QR code for the patient's questionnaire link",
}: {
  readonly clinicName: string;
  /** The full link, with its token in the fragment when it has one. */
  readonly url: string;
  /** "11:59 p.m. on Thu, Oct 8" (codeExpiryLabel), when the issue response says. */
  readonly expiry?: string | undefined;
  /** The line under the code telling the patient what scanning it does. */
  readonly scanText?: string;
  /** The code's accessible name. */
  readonly qrLabel?: string;
}) {
  return (
    <section className="walk-in-code-slip" aria-label="Code for the patient">
      <p className="walk-in-code-clinic">{clinicName}</p>
      <QrImage url={url} label={qrLabel} />
      <p className="walk-in-code-scan">{scanText}</p>
      <p className="walk-in-code-link">{shortPatientLink(url)}</p>
      {expiry === undefined ? null : <p className="walk-in-code-expiry">Works until {expiry}</p>}
    </section>
  );
}
